<template>
  <div id="image_processor_container" :class="containerClass">
    <h4>Image {{ imageNumber + 1 }}<span>{{ statusText }}</span></h4>
    <div v-if="config.showLicensed" class="checkbox pull-left">
      <label><input v-model="licensed" type="checkbox" class="pull-left" :disabled="isSaving || isSuccess"> Licensed <span class="text-muted">(<a target="_blank" href="https://docs.google.com/document/d/1RShh9NxuZHvTRUmk6SQvHC8OV-XM2ev1Aw8CCTMQRtE/edit?usp=sharing">may be shared with data partners</a>)</span></label>
    </div>
    <br />
    <input v-model="source" type="text" class="form-control" placeholder="Source">
    <br />
    <p>Enter file URL or upload file</p>
    <div class="file-upload-container">
      <input v-model="imageUrl" type="text" class="form-control file-upload-option" placeholder="Image URL" :disabled="isSaving || isSuccess" @change="saveFromUrl">
      <!-- file drag/drop upload shamlessly pilfered from https://scotch.io/tutorials/how-to-handle-file-uploads-in-vue-2 -->
      <form enctype="multipart/form-data" novalidate class="file-upload-option">
        <div class="dropbox">
          <input 
            ref="fileInput"
            type="file" 
            :disabled="isSaving || isSuccess"
            :accept="config.imageRequirements.allowedFileTypesForInput" 
            @change="saveFromFile($event.target.name, $event.target.files);"
            class="input-file"
          >
            <p v-if="isInitial || isFailed">
              Drag a new file here <br> or click to browse
            </p>
            <p v-if="isSaving">
              Uploading file...
            </p>
            <p v-if="isSuccess">
              Uploaded
            </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
  const STATUS_INITIAL = 0, STATUS_SAVING = 1, STATUS_SUCCESS = 2, STATUS_FAILED = 3;
  // can set default values based on the parent table name
  const licensedInit = {
    submitted_photos_staging: true,
    survey_photos_staging: true
  };
  const sourceInit = {
    submitted_photos_staging: 'Submitted to BP',
    survey_photos_staging: 'Submitted to BP'
  };
  export default {
    name: 'ImageProcessor',
    props: {
        parentTable: String,
        rowData: Object,
        colToUpdate: String,
        config: Object,
        imageNumber: Number,
        clearForm: Boolean
    },
    data() {
      return {
        currentStatus: STATUS_INITIAL,
        licensed: licensedInit[this.parentTable] !== undefined ? licensedInit[this.parentTable] : null,
        source: sourceInit[this.parentTable] !== undefined ? sourceInit[this.parentTable] : null,
        imageUrl: null,
        minImageSizeBytes: this.config.imageRequirements.minImageSizeKb * 1000,
      }
    },
    computed: {
      isInitial() { return this.currentStatus === STATUS_INITIAL; },
      isSaving() { return this.currentStatus === STATUS_SAVING; },
      isSuccess() { return this.currentStatus === STATUS_SUCCESS; },
      isFailed() { return this.currentStatus === STATUS_FAILED; },
      statusText() {
        let statusText = '';
        if (this.isSaving) 
            statusText = ': saving...';
        else if (this.isFailed) 
            statusText = ': error';
        else if (this.isSuccess)
            statusText = ': success';
        return statusText;
      },
      containerClass() {
        let className = '';
        if (this.isSuccess) className = 'success';
        else if (this.isFailed) className = 'error';
        else if (this.isInitial) className = 'initial';
        return className; 
      }
    },
    methods: {
      /**
       * Sets form status to initial. Resets all form inputs.
       */
      reset() {
        // reset form to initial state
        this.currentStatus = STATUS_INITIAL;
        this.source = sourceInit[this.parentTable] !== undefined ? sourceInit[this.parentTable] : null;
        this.licensed = licensedInit[this.parentTable] !== undefined ? licensedInit[this.parentTable] : null,
        this.imageUrl = null;
      },
      /**
       * Sets form status to failed. Alerts the message. Resets file input form, so that user can re-attempt uploading same photo.
       * @param {string} message the message to display in the alert
       */
      hasUploadError(message) {
        this.currentStatus = STATUS_FAILED;
        alert(message);
        this.$refs.fileInput.value = '';
      },
      /**
       * @param {File} file File object containing a "type" property, which is a string such as 'image/jpg'
       * @returns true if the file is an allowed file type; otherwise false
       */
      checkFileType(file) {
        if (this.config.imageRequirements.allowedFileTypesForInput === 'image/*' && !file.type.includes('image/')) {
          return false;
        } else if (this.config.imageRequirements.allowedFileTypesForInput !== 'image/*' && this.config.imageRequirements.allowedFileTypesForInput !== file.type) {
          return false;
        }
        return true;
      },
      /**
       * Determines whether the file meets the minimum size requirements in bytes.
       * @param {File} file File object containing a "size" property, which is in bytes
       * @returns true if the file size is greater than the minimum; otherwise false
       */
      checkImageSizeBytes(file) {
        return file.size >= this.minImageSizeBytes;
      },
      /**
       * Given an image file, returns an object containing its width and height in pixels
       * Inspired by https://stackoverflow.com/questions/72534317/javascript-how-can-i-get-image-width-and-height-from-a-blob.
       * @param {File} file 
       * @returns {Promise<{width: integer, height: integer}>}
       */
      async getImageDimensions(file) {
        return new Promise(resolve => {
          const reader = new FileReader();
          reader.addEventListener('load', (event) => {
            const image = new Image();
            image.src = event.currentTarget.result;
            image.onload = function() {
              resolve({ 
                width: this.naturalWidth, 
                height: this.naturalHeight
              });
            }
          });
          reader.readAsDataURL(file);
        });
      },
      /**
       * Determines whether the file meets the minimum size in pixels (both width and height).
       * @async
       * @param {{width: integer, height: integer}} fileDimensions 
       * @param {integer} minImageHeight minimum image height in pixels
       * @param {integer} minImageWidth minimum image width in pixels
       * @returns {Promise<boolean>}
       */
      async checkImageDimensions(fileDimensions, minImageWidth, minImageHeight) {
        return fileDimensions.width >= minImageWidth && fileDimensions.height >= minImageHeight;
      },
      /**
       * Throws an error if the file doesn't meet requirements for size, type, etc
       * @param {File} file 
       */
      async checkImage(file) {
        if (!this.checkFileType(file)) {
          throw Error('Incorrect file type.');
        }

        if (!this.checkImageSizeBytes(file)) {
          throw Error(`Incorrect size. Image is ${Math.round(file.size / 1000)} KB but must be at least ${this.config.imageRequirements.minImageSizeKb} KB.`);
        }

        const fileDimensions = await this.getImageDimensions(file);
        const meetsMinimumDimensions = await this.checkImageDimensions(fileDimensions, this.config.imageRequirements.minImageWidth, this.config.imageRequirements.minImageHeight);
        if (!meetsMinimumDimensions) {
          throw Error(`Photo dimensions are too small. Photo is ${fileDimensions.width} x ${fileDimensions.height} but minimum size is ${this.config.imageRequirements.minImageWidth} x ${this.config.imageRequirements.minImageHeight} (width x height).`);
        }
      },
      /**
       * Returns a file name from a URL. Removes the query string if it exists.
       * @param {string} url URL such as https://test.com/somephoto.jpg
       * @returns {string} file name extracted from the URL, such as somephoto.jpg
       */
      getFileName(url) {
        let fileName = url.split('/').pop();
        fileName = fileName.split('?')[0];
        return fileName;
      },
      /**
       * Adds a current timestamp to the end of the filename
       * @param {string} filename, such as somephoto.jpg
       * @returns {string} filename with the added timestamp, such as somephoto_2026-04-17_143022.jpg
       */
      appendCurrentTimestamp(filename) {
        // timetamp is originally in this format: "2026-04-17T14:30:22.000Z"
        const timestamp = new Date().toISOString().slice(0, 19).replace('T', '_').replace(/:/g, '');
        const nameWithoutExtension = filename.split('.')[0];
        const fileExtension = filename.split('.').pop();
        return `${nameWithoutExtension}_${timestamp}.${fileExtension}`;
      },
      /**
       * The workhorse of this component. 
       * Saves the file to S3; inserts file information to the files table; and passes along metadata [add screen] or updates table [edit screen]
       * @async
       * @param {File} file 
       */
      async save(file) {
        if (this.config.requireSource && (!this.source || !this.source.trim())) {
          return this.hasUploadError('Source is required');
        }
        this.currentStatus = STATUS_SAVING;
        try {
          await this.checkImage(file);
        } catch (e) {
          return this.hasUploadError(e.message);
        }
        
        // Clicking "save" initiates 4-step upload process:
        // 1. Hit the API/files/upload endpoint. It might thrown an error if the filename already exists, otherwise move on to step 2.
        let headers = {'Content-Type': 'application/json', 'Accept': 'application/json'};
        headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/files/upload`;

        let response1 = await fetch(`${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
          method: "POST",
          headers: headers,
          credentials: 'include',
          body: JSON.stringify({
            filename: file.name,
            mimetype: file.type
          })
        });
        response1 = await response1.json(); // Convert response to JSON
        if(!response1.data || !response1.success) {
          return this.hasUploadError(response1.message);
        }

        // 2. Upload the file to the S3 enpoint returned by the previous step.
        const formdata = new FormData();
        for(const key in response1.data) formdata.append(key, ""+response1.data[key]);
        formdata.append("file", file);

        let response2 = await fetch('https://ballotpedia-api4.s3.amazonaws.com/', {
          method: 'POST',
          headers: { 'ACL': 'public-read' },
          body: formdata
        });
        if(!response2.ok) {
          return this.hasUploadError(response2.statusText);
        }
        await this.sleep(6000); // wait two seconds for the thumbnails to be generated

        headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/files/save`;

        // 3. Hit the API/files/save endpoint to save the file in the "files" table. This will return a file ID.
        let response3 = await fetch(`${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
          method: "POST",
          headers: headers,
          credentials: 'include',
          body: JSON.stringify({
            filename: file.name,
            licensed: this.licensed ? this.licensed : false,
            source: this.source ? this.source : null,
            tags: null
          })
        });
        response3 = await response3.json(); // Convert response to JSON
        if(!response3.data || !response3.success) {
          return this.hasUploadError(response3.message);
        }

        // ***Skip updating the record if we're adding, since no person ID available yet***

        if (!this.$route.path.includes('add')) {
          // 4. Hit the API/crud/update endpoint to associate the new file ID with the person/row we're editing.
          let dataObject = {}; // Create Payload
          let method, payload;

          if (this.parentTable === 'tiktok_people_photos') {
            dataObject.person = parseInt(this.rowData.person.id);
            dataObject.file = parseInt(response3.data.id);
            payload = {
              data: [dataObject],
              table: this.parentTable
            };
            method = 'POST';
            headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/crud/insertOne`; // use insertOne so we get the ID back
          } else { // people etc
            dataObject.id = parseInt(this.rowData.id);
            dataObject[this.colToUpdate] = parseInt(response3.data.id);
            payload = {
              data: [dataObject],
              table: this.parentTable
            };
            method = 'PATCH';
            headers.resource = `${process.env.VUE_APP_API4_DOMAIN}/crud`;
          }

          console.log("File Upload Payload");
          console.log(payload);


          let response4 = await fetch(`${process.env.VUE_APP_API4_DOMAIN}/users/authorize`, {
            method: method,
            headers: headers,
            credentials: 'include',
            body: JSON.stringify(payload)
          });
          response4 = await response4.json(); // Convert response to JSON

          if(!response4.data || !response4.success) {
            const errorMessage = JSON.parse(response4.message).message;
            return this.hasUploadError(errorMessage);
          }
          
          // 5. When all of this is done, we can close the modal (if only one photo to upload) and update the cell value with the file info
          this.currentStatus = STATUS_SUCCESS;
          const photoInfo = {
            id: this.parentTable === 'tiktok_people_photos' ? parseInt(response4.data.id) : parseInt(response3.data.id), // tiktok_people_photos.id or files.id
            name: file.name
          }
          this.$emit('update', photoInfo);
          
        } else {
            // If doing an add, we want to create an object we can attach to the cell as meta data.
            // That way, we have access to the file ID that we can use in the add payload.
            this.currentStatus = STATUS_SUCCESS;
            const photoInfo = {
              file: response3.data.id,
              photo: file.name
            }
            this.$emit('update', photoInfo);
        }
      },
      /**
       * Given a URL from the form, downloads the image, compresses it if it's too big, and calls the save function.
       * If there's an error, shows an alert message.
       * @async
       */
      async saveFromUrl() {
        if (!this.imageUrl) return;

        try {
          this.source = this.imageUrl;
          const blob = await window.imageConversion.urltoBlob(this.imageUrl)
          const compressed = await window.imageConversion.compressAccurately(blob, this.config.imageRequirements.maxCompressionSizeKb);
          let fileName = this.getFileName(this.imageUrl);
          fileName = this.appendCurrentTimestamp(fileName);
          const file = new File([compressed], fileName, {type: compressed.type});
          await this.save(file);
        } catch (e) {
          // show a more helpful error message if there's a fetch error due to CORS
          if (e.message === 'Failed to fetch') {
            e.message = 'Could not download file'
          }
          return this.hasUploadError(e.message);
        }
      },
      /**
       * Given a file from the upload form, compresses it if it's too big and calls the save function.
       * If there's an error, shows an alert message.
       * @param {string} fieldName form input name
       * @param {FileList} fileList array-like object provided by file input
       */
      async saveFromFile(fieldName, fileList) {
        const formData = new FormData();
        if (!fileList.length) return;
        // append the files to FormData
        Array
          .from(Array(fileList.length).keys())
          .map(x => {
            formData.append(fieldName, fileList[x], fileList[x].name.replace(/ /g,"_"));
          });

        try {
          let file = formData.values().next().value;
          const fileNameWithTimestamp = this.appendCurrentTimestamp(file.name);
          const compressed = await window.imageConversion.compressAccurately(file, this.maxCompressionSizeKb);
          file = new File([compressed], fileNameWithTimestamp, {type: compressed.type});
          await this.save(file);
        } catch (e) {
          return this.hasUploadError(e.message);
        }
      },
      /**
       * Waits for the specified number of milliseconds.
       * @param {integer} ms number of milliseconds to wait
       */
      sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
      }
    },
    watch: {
      /**
       * Watches for the modal being closed in the parent, so that we can clear the form.
       * Prevents old data being in the form when it is re-opened.
       * Sends a message back to the parent, so that it can stop telling all its children to clear the form.
       * @param {boolean} val 
       */
      clearForm(val) {
        if (val) {
          this.reset();
          this.$emit('formHasCleared');
        }
      }
    }
  }
</script>

<style scoped>
#image_processor_container {
  padding: 0.5em;
  margin: 0.5em 0em;
  border: 1px solid dimgray;
}
.initial {
  background: #F9F9F9;
}
.success {
  background: #dcedc8;
}
.error {
  background: #fd9b9b;
}
.dropbox {
  outline: 2px dashed grey; /* the dash box */
  outline-offset: -10px;
  background: lightcyan;
  color: dimgray;
  padding: 10px 10px;
  min-height: 200px; /* minimum height */
  /* position: relative; */
  cursor: pointer;
  border-radius:5px;
}
.dropbox:hover {
  background: lightblue; /* when mouse over to the drop zone, change color */
}
.dropbox p {
  font-size: 1.2em;
  text-align: center;
  padding: 50px 0;
}
.input-file {
  opacity: 0; /* invisible but it's there! */
  width: 100%;
  height: 200px;
  position: absolute;
  cursor: pointer;
}
.file-upload-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}
.file-upload-option {
  width: 50%;
  margin: 0.5em;
}
</style>